import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  ColorSchemeScript,
  MantineProvider,
  localStorageColorSchemeManager,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { App } from './App';
import { ApiCallError } from './api/client';
import { notifySuccess, notifyError } from './lib/notify';
import { translateStatic, type TranslationKey } from './i18n/translations';
import { SessionProvider } from './auth/SessionContext';
import { LocaleProvider } from './i18n/LocaleContext';
import { transpayTheme } from './theme';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';

/**
 * Per-mutation wording / opt-out via React Query `meta`. Prefer i18n KEYS so
 * toasts are multilingual (resolved at toast time against the active locale):
 *   meta: { successKey: 'notify.user.update.ok', errorKey: 'notify.user.update.fail' }
 *   meta: { successNotification: false }   // suppress the global success toast
 *   meta: { errorNotification: false }     // suppress the global error toast
 * `successMessage` / `errorMessage` (literal, already-localized strings) are
 * still honoured for dynamic cases.
 */
type MutationMetaShape = {
  successNotification?: boolean;
  errorNotification?: boolean;
  successKey?: string;
  errorKey?: string;
  successMessage?: string;
  errorMessage?: string;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      // Don't burn a retry on an auth failure — let the session-expiry guard
      // redirect to /login right away instead of retrying a doomed request.
      retry: (failureCount, error) =>
        error instanceof ApiCallError && error.status === 401 ? false : failureCount < 1,
      refetchOnWindowFocus: true,
    },
  },
  // Single, consistent global toast for EVERY mutation result — success AND
  // failure — so action feedback looks identical and is visible from any page.
  // Per-action wording comes from the mutation `meta`; components opt out only
  // when they render their own (e.g. a detailed custom toast).
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const meta = mutation.options.meta as MutationMetaShape | undefined;
      if (meta?.successNotification === false) return;
      const msg = meta?.successKey
        ? translateStatic(meta.successKey as TranslationKey)
        : meta?.successMessage;
      notifySuccess(msg);
    },
    onError: (error, _vars, _ctx, mutation) => {
      const meta = mutation.options.meta as MutationMetaShape | undefined;
      if (meta?.errorNotification === false) return;
      const title = meta?.errorKey
        ? translateStatic(meta.errorKey as TranslationKey)
        : meta?.errorMessage;
      notifyError(error, title);
    },
  }),
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

/**
 * Persist the user's color-scheme choice across reloads via localStorage.
 * Mantine reads/writes 'light' | 'dark' | 'auto' here; 'auto' follows the OS
 * preference reported by `prefers-color-scheme`.
 */
const colorSchemeManager = localStorageColorSchemeManager({
  key: 'transpay_color_scheme',
});

createRoot(rootEl).render(
  <StrictMode>
    <ColorSchemeScript defaultColorScheme="auto" />
    <MantineProvider
      theme={transpayTheme}
      defaultColorScheme="auto"
      colorSchemeManager={colorSchemeManager}
    >
      <Notifications position="top-right" />
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <LocaleProvider>
              <SessionProvider>
                <App />
              </SessionProvider>
            </LocaleProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </StrictMode>,
);
